import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, distinctUntilChanged, Observable, of} from 'rxjs';
import {filter, map, tap} from "rxjs/operators";
import {StorageService} from "../storage/storage.service";
import {isFalse, isTrue, safeSplit} from "../../libs/utils/epic.utils";
import {environment} from "../../../environments/environment";
import {AuthRepo} from "../../repositories/api/auth.repo";

@Injectable({providedIn: 'root'})
export class AuthService {

  private static readonly LEGACY_TOKEN_PREFIX: string = 'auth:';

  private readonly authRepo: AuthRepo = inject(AuthRepo);
  private readonly storageService: StorageService = inject(StorageService);

  private readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  private readonly authenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.removeLegacyTokens();
    if (this.isIronBackupViewer) {
      this.authenticated.next(this.isAuthenticated());
    }
  }

  public isAuthenticated(): boolean {
    if (this.isIronBackupViewer) {
      return true;
    }
    return this.authenticated.value;
  }

  public getAuthenticated(): Observable<boolean> {
    return this.authenticated.asObservable();
  }

  public getAuthenticatedSuccess(): Observable<boolean> {
    return this.getAuthenticated().pipe(
      filter((b) => isTrue(b)),
      distinctUntilChanged()
    );
  }

  public authenticate(url: string): Observable<boolean> {
    return this.authRepo.readSession().pipe(
      map(() => true),
      catchError(() => of(false)),
      tap((authenticated) => {
        this.authenticated.next(authenticated);
        if (isFalse(authenticated)) {
          this.redirect(this.authRepo.buildLoginUrl(url));
        }
      })
    );
  }

  public logOut(): void {
    this.authenticated.next(false);
    this.deleteAllCookies();
    this.storageService.cleanAll();
    this.authRepo.logout().subscribe({
      next: (w) => this.redirect(w.getFirstData().logoutUrl),
      error: () => this.redirect(this.authRepo.buildLoginUrl('/'))
    });
  }

  private redirect(url: string): void {
    window.location.href = url;
  }

  private removeLegacyTokens(): void {
    Object.keys(localStorage).filter((key) => key.startsWith(AuthService.LEGACY_TOKEN_PREFIX)).forEach((key) => localStorage.removeItem(key));
  }

  private deleteAllCookies(): void {
    const cookies: string[] = safeSplit(document.cookie, ';');
    for (const cookie of cookies) {
      const eqPos: number = cookie.indexOf("=");
      const name: string = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  }
}
